import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { offersAPI } from '../../lib/api';
import { getScoreColor } from '../../lib/scoreUtils';

export default function CandidateAssessmentsTab({ candidate, candidateId, parsed, match, scoreBreakdown, matchedSkillsSet, depthAnalysis, analyzingDepth, runDepthAnalysis, offerProb, pipelineHistory, offer, showOfferForm, setShowOfferForm, offerForm, setOfferForm, onboardingPlan, setOnboardingPlan, loadCandidate }) {
  return (
        <TabsContent value="assessments">
          {/* Sent invitation links */}
          {(() => {
            const assessInvites = (candidate.sent_invitations || []).filter(n => n.type === 'assessment_assigned');
            if (assessInvites.length > 0) return (
              <div style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#64748B', marginBottom: '0.625rem' }}>Sent Assessment Links</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {assessInvites.map(inv => (
                    <div key={inv._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 10, background: '#FFE7D9', border: '1px solid #BBE3F5' }}>
                      <div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3' }}>{inv.data?.assessment_title || 'Assessment'}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>
                          Sent: {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''} &middot; {inv.data?.job_title || ''}
                          {inv.email_sent && <span style={{ color: '#059669', marginLeft: '0.375rem' }}>Email sent</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        {inv.data?.access_code && (
                          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/take-assessment/${inv.data.access_code}`); toast.success('Assessment link copied!'); }}
                            style={{ padding: '0.3125rem 0.625rem', borderRadius: 6, border: '1px solid #BBE3F5', background: '#fff', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600, color: '#0076A3' }}>
                            Copy Link
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ); return null;
          })()}

          {(candidate.assessment_attempts || []).length === 0 && (candidate.sent_invitations || []).filter(n => n.type === 'assessment_assigned').length === 0 ? (
            <div className="empty-state" style={{ paddingTop: '2rem' }}>
              <p>No assessments sent or taken by this candidate yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Attempt stats banner */}
              {candidate.assessment_stats && candidate.assessment_stats.total_attempts > 0 && (
                <div style={{ display: 'flex', gap: '1rem', padding: '1rem', borderRadius: 12, background: '#F1EAE1', border: '1px solid #ECE7DE' }}>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3' }}>{candidate.assessment_stats.total_attempts}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>Total Attempts</div>
                  </div>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#059669' }}>{candidate.assessment_stats.best_score != null ? candidate.assessment_stats.best_score + '%' : '—'}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#059669', fontWeight: 600 }}>Best Score</div>
                  </div>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3' }}>{candidate.assessment_stats.latest_score != null ? candidate.assessment_stats.latest_score + '%' : '—'}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>Latest Score</div>
                  </div>
                </div>
              )}

              {(candidate.assessment_attempts || []).map((att, attemptIdx) => {
                const scored = att.status === 'scored';
                const summary = att.summary || {};
                const isBest = scored && att.percentage === candidate.assessment_stats?.best_score;
                return (
                  <Card key={att._id} style={{ background: '#fff', border: `1px solid ${isBest ? '#BBF7D0' : '#ECE7DE'}`, borderRadius: 14, padding: '1.5rem', position: 'relative' }}>
                    {isBest && <div style={{ position: 'absolute', top: -8, right: 16, background: '#059669', color: '#fff', padding: '0.125rem 0.625rem', borderRadius: 999, fontSize: '0.625rem', fontWeight: 700 }}>Best Score</div>}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div>
                        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#0076A3' }}>
                          Attempt {(candidate.assessment_attempts || []).length - attemptIdx} — {att.assessment_title || 'Assessment'}
                        </h4>
                        <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
                          {att.candidate_email} &middot; {att.started_at ? new Date(att.started_at).toLocaleDateString() : ''}
                          &middot; Status: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{att.status}</span>
                        </div>
                      </div>
                      {att.percentage != null && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: att.percentage >= 70 ? '#059669' : att.percentage >= 50 ? '#D97706' : '#DC2626' }}>
                            {att.percentage}%
                          </div>
                          <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>{att.total_score}/{att.total_possible} pts</div>
                        </div>
                      )}
                    </div>

                    {/* Verified skills — what this assessment actually proved (>=80%) */}
                    {scored && (att.verified_skills || []).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Verified Skills · proven ≥80%</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {(att.verified_skills || []).map((s) => (
                            <span key={`vs-${att._id}-${s}`} data-testid="assessment-verified-skill"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.25rem 0.6rem', borderRadius: 999, background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.28)', fontSize: '0.6875rem', fontWeight: 700, color: '#047857' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Score breakdown per question */}
                    {scored && (att.scores || []).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', marginBottom: '0.625rem' }}>Questions & Answers</h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {(att.questions || []).map((q, qi) => {
                            const s = (att.scores || [])[qi] || {};
                            const answer = (att.answers || [])[qi] || '(No answer)';
                            const timeSpent = (att.time_per_question || [])[qi] || 0;
                            const isMCQ = q.type === 'multiple_choice';
                            const isCorrect = isMCQ && s.is_correct;
                            const scorePct = (s.max_points || 1) > 0 ? (s.score || 0) / (s.max_points || 1) : 0;

                            return (
                              <div key={`qa-${att._id}-${qi}`} style={{ borderRadius: 10, border: '1px solid #ECE7DE', overflow: 'hidden' }}>
                                {/* Question header */}
                                <div style={{ padding: '0.75rem 1rem', background: '#FAF7F2', borderBottom: '1px solid #ECE7DE', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.625rem', color: '#94A3B8' }}>Q{qi + 1}</span>
                                      <span style={{ padding: '0.0625rem 0.375rem', borderRadius: 999, fontSize: '0.5625rem', fontWeight: 600, textTransform: 'capitalize',
                                        background: isMCQ ? 'rgba(30,104,255,0.08)' : q.type === 'coding' ? 'rgba(124,58,237,0.08)' : 'rgba(217,119,6,0.08)',
                                        color: isMCQ ? '#0076A3' : q.type === 'coding' ? '#F2600C' : '#D97706' }}>
                                        {q.type?.replace('_', ' ')}
                                      </span>
                                      <span style={{ fontSize: '0.5625rem', color: '#94A3B8' }}>{q.skill} &middot; {q.difficulty} &middot; {q.points}pts &middot; {timeSpent}s</span>
                                    </div>
                                    <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3', lineHeight: 1.5 }}>{q.question}</p>
                                  </div>
                                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1rem', fontWeight: 700, color: scorePct >= 0.7 ? '#059669' : scorePct >= 0.5 ? '#D97706' : '#DC2626' }}>
                                      {s.score}/{s.max_points}
                                    </div>
                                  </div>
                                </div>

                                {/* Answer section */}
                                <div style={{ padding: '0.75rem 1rem' }}>
                                  {isMCQ ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                                      {(q.options || []).map((opt, oi) => {
                                        const isSelected = answer === opt;
                                        const isCorrectOpt = opt === q.correct_answer;
                                        let bg = '#fff', border = '#ECE7DE', color = '#4A5568';
                                        if (isCorrectOpt) { bg = '#F0FDF4'; border = '#BBF7D0'; color = '#059669'; }
                                        if (isSelected && !isCorrectOpt) { bg = '#FEF2F2'; border = '#FECACA'; color = '#DC2626'; }
                                        return (
                                          <div key={`opt-${att._id}-${qi}-${oi}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 8, border: `1px solid ${border}`, background: bg }}>
                                            <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isSelected ? color : '#CBD5E1'}`, background: isSelected ? color : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                              {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                            </span>
                                            <span style={{ fontSize: '0.8125rem', color }}>{opt}</span>
                                            {isCorrectOpt && <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#059669', marginLeft: 'auto' }}>Correct</span>}
                                            {isSelected && !isCorrectOpt && <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#DC2626', marginLeft: 'auto' }}>Selected</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={{ padding: '0.625rem 0.875rem', borderRadius: 8, background: '#FAF7F2', border: '1px solid #F0EBE3', fontSize: '0.8125rem', color: '#4A5568', whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 200, overflow: 'auto', fontFamily: q.type === 'coding' ? 'JetBrains Mono, monospace' : 'inherit' }}>
                                      {answer}
                                    </div>
                                  )}

                                  {/* AI Feedback */}
                                  {s.feedback && (
                                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>
                                      <strong>Feedback:</strong> {s.feedback}
                                    </div>
                                  )}
                                  {(s.strengths || []).length > 0 && (
                                    <div style={{ marginTop: '0.375rem', fontSize: '0.6875rem', color: '#059669' }}>
                                      <strong>Strengths:</strong> {s.strengths.join(', ')}
                                    </div>
                                  )}
                                  {(s.weaknesses || []).length > 0 && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: '#D97706' }}>
                                      <strong>Areas to improve:</strong> {s.weaknesses.join(', ')}
                                    </div>
                                  )}
                                  {s.ai_generated_likelihood > 0.3 && (
                                    <div style={{ marginTop: '0.375rem', fontSize: '0.6875rem', color: '#DC2626', fontWeight: 600 }}>
                                      AI-generated likelihood: {(s.ai_generated_likelihood * 100).toFixed(0)}%
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Skill breakdown from summary */}
                    {summary.skill_breakdown && Object.keys(summary.skill_breakdown).length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Skill Breakdown</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {Object.entries(summary.skill_breakdown).map(([skill, data]) => (
                            <div key={`skill-${att._id}-${skill}`} style={{ padding: '0.375rem 0.75rem', borderRadius: 8, background: '#F1EAE1', border: '1px solid #ECE7DE', fontSize: '0.75rem' }}>
                              <span style={{ fontWeight: 600, color: '#0076A3' }}>{skill}</span>
                              <span style={{ color: '#64748B', marginLeft: '0.375rem' }}>{data.score}/{data.max}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Integrity */}
                    {summary.integrity_assessment && (
                      <div style={{ padding: '0.625rem 0.875rem', borderRadius: 8, background: summary.integrity_assessment.risk_level === 'low' ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${summary.integrity_assessment.risk_level === 'low' ? '#BBF7D0' : '#FDE68A'}`, fontSize: '0.75rem' }}>
                        <span style={{ fontWeight: 600, color: summary.integrity_assessment.risk_level === 'low' ? '#059669' : '#D97706' }}>
                          Integrity: {summary.integrity_assessment.risk_level} risk
                        </span>
                        {summary.integrity_assessment.summary && <span style={{ color: '#64748B', marginLeft: '0.5rem' }}> — {summary.integrity_assessment.summary}</span>}
                      </div>
                    )}

                    {/* AI Summary */}
                    {summary.summary && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#4A5568', lineHeight: 1.7 }}>
                        <strong>AI Summary:</strong> {summary.summary}
                      </div>
                    )}

                    {/* Anti-cheat events */}
                    {(att.anti_cheat_events || []).length > 0 && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.6875rem', color: '#D97706' }}>
                        {att.anti_cheat_events.length} anti-cheat event{att.anti_cheat_events.length !== 1 ? 's' : ''} logged
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
  );
}